import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { downloadBlob } from "@/lib/download";
import PageHeader from "@/components/common/PageHeader";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import SearchSelect from "@/components/common/SearchSelect";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { DraftsPanel, saveDraft, discardDraft } from "@/components/DraftsPanel";
import { fmtINR, fmtDate, today } from "@/lib/format";
import { Plus, Trash2 } from "lucide-react";
import Attachments from "@/components/Attachments";
import AttachButton from "@/components/AttachButton";
import StagedAttachments from "@/components/StagedAttachments";

const VOUCHER_TYPES = [
  { v: "journal", l: "Journal" },
  { v: "payment", l: "Payment" },
  { v: "receipt", l: "Receipt" },
  { v: "contra", l: "Contra" },
];

export default function Vouchers() {
  const stagedRef = React.useRef();
  const [rows, setRows] = useState([]);
  const [type, setType] = useState("");
  const [openNew, setOpenNew] = useState(false);
  const [draftId, setDraftId] = useState(null);
  const [draftsKey, setDraftsKey] = useState(0);
  const saveAsDraft = async () => {
    try {
      await saveDraft("journal", { ...form, lines: form.lines.filter((l) => l.account_id && (l.debit || l.credit)) }, form, draftId);
      setDraftId(null); setOpenNew(false); setDraftsKey((k) => k + 1);
      setForm((f) => ({ ...f, narration: "", lines: [{ account_id: "", debit: 0, credit: 0 }, { account_id: "", debit: 0, credit: 0 }] }));
    } catch (e) { const d = e?.response?.data?.detail; toast.error(typeof d === "string" ? d : "Could not save draft"); }
  };
  const [openDetail, setOpenDetail] = useState(null);   // voucher for attachments
  const [accounts, setAccounts] = useState([]);
  const [parties, setParties] = useState([]);
  const [branches, setBranches] = useState([]);
  const [bizSegments, setBizSegments] = useState([]);
  const [geoSegments, setGeoSegments] = useState([]);
  const [departments, setDepartments] = useState([]);
  // Iter-199 · multi-select for bulk + wipe delete
  const [selected, setSelected] = useState(new Set());
  const clearSel = () => setSelected(new Set());
  const toggleOne = (id) => {
    const n = new Set(selected);
    n.has(id) ? n.delete(id) : n.add(id);
    setSelected(n);
  };
  const toggleAll = () => {
    if (selected.size === rows.length) setSelected(new Set());
    else setSelected(new Set(rows.map((r) => r.id)));
  };
  const bulkDelete = async () => {
    if (!selected.size) return;
    if (!window.confirm(`Erase ${selected.size} voucher(s)?\n\nVouchers are soft-deleted (audit trail preserved · GL balances updated). Locked vouchers are skipped.`)) return;
    try {
      const r = await api.post("/vouchers/bulk-delete", { voucher_ids: Array.from(selected) });
      toast.success(`Erased ${r.data.soft_deleted} voucher(s)${r.data.blocked?.length ? ` · ${r.data.blocked.length} locked skipped` : ""}`);
      clearSel(); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Bulk delete failed"); }
  };
  const wipeAll = async () => {
    const phrase = window.prompt(`⚠︎ Erase EVERY voucher in this company?\n\nAll non-locked vouchers will be soft-deleted (audit trail preserved).\nLocked / period-closed vouchers survive.\n\nType   WIPE ALL DATA   to confirm:`);
    if (phrase !== "WIPE ALL DATA") { if (phrase !== null) toast.error("Confirmation phrase did not match — nothing wiped"); return; }
    try {
      const r = await api.post("/vouchers/wipe-all", { confirm: "WIPE ALL DATA" });
      toast.success(`Wiped ${r.data.soft_deleted} voucher(s) · ${r.data.locked_left} locked remain`);
      clearSel(); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Wipe failed"); }
  };
  const [form, setForm] = useState({ voucher_type: "journal", date: today(), narration: "",
    business_segment_id: "", geo_segment_id: "", department_id: "",
    lines: [
      { account_id: "", debit: 0, credit: 0, narration: "", party_id: "" },
      { account_id: "", debit: 0, credit: 0, narration: "", party_id: "" },
    ], branch_id: "" });

  const [filters, setFilters] = useState({
    from_date: "", to_date: "", party_id: "", account_id: "",
    narration: "", min_amount: "", max_amount: "", source: "",
  });
  const load = async () => {
    const params = { ...(type ? { voucher_type: type } : {}) };
    Object.entries(filters).forEach(([k, v]) => { if (v !== "" && v !== null) params[k] = v; });
    const r = await api.get("/vouchers", { params });
    setRows(r.data);
  };
  const resetFilters = () => setFilters({
    from_date: "", to_date: "", party_id: "", account_id: "",
    narration: "", min_amount: "", max_amount: "", source: "",
  });

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [type]);
  useEffect(() => {
    api.get("/accounts").then((r) => setAccounts(r.data));
    api.get("/parties").then((r) => setParties(r.data));
    api.get("/branches").then((r) => { setBranches(r.data); setForm((f) => ({ ...f, branch_id: r.data[0]?.id || "" })); });
    api.get("/segments/business").then((r) => setBizSegments(r.data || []));
    api.get("/segments/geo").then((r) => setGeoSegments(r.data || []));
    api.get("/departments").then((r) => setDepartments(r.data || [])).catch(() => setDepartments([]));
  }, []);

  // Any income/expense line? → segment header tags required
  const hasIncExpLine = () => form.lines.some(l => {
    const a = accounts.find(x => x.id === l.account_id);
    return a && (a.account_type === "income" || a.account_type === "expense");
  });
  const autoFillHO = () => {
    const bizHO = bizSegments.find(s => s.is_head_office)?.id;
    const geoHO = geoSegments.find(s => s.is_head_office)?.id;
    setForm(f => ({ ...f, business_segment_id: f.business_segment_id || bizHO || "",
                            geo_segment_id: f.geo_segment_id || geoHO || "" }));
  };

  const totalDr = form.lines.reduce((s, l) => s + Number(l.debit || 0), 0);
  const totalCr = form.lines.reduce((s, l) => s + Number(l.credit || 0), 0);
  const balanced = Math.abs(totalDr - totalCr) < 0.01 && totalDr > 0;

  const updateLine = (i, patch) => setForm((f) => ({ ...f, lines: f.lines.map((ln, j) => (i === j ? { ...ln, ...patch } : ln)) }));
  const addLine = () => setForm((f) => ({ ...f, lines: [...f.lines, { account_id: "", debit: 0, credit: 0, narration: "", party_id: "" }] }));
  const removeLine = (i) => setForm((f) => ({ ...f, lines: f.lines.filter((_, j) => j !== i) }));

  const submit = async () => {
    if (!balanced) return toast.error("Voucher must be balanced (Debit = Credit, non-zero)");
    try {
      const payload = { ...form, lines: form.lines.filter((l) => l.account_id && (l.debit || l.credit)) };
      const res = await api.post("/vouchers", payload);
      await stagedRef.current?.flush("voucher", res.data.id);
      await discardDraft(draftId); setDraftId(null); setDraftsKey((k) => k + 1);
      toast.success("Voucher posted");
      setOpenNew(false);
      setForm((f) => ({ ...f, narration: "", lines: [{ account_id: "", debit: 0, credit: 0 }, { account_id: "", debit: 0, credit: 0 }] }));
      load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d && typeof d === "object" && d.error === "segments_required") {
        toast.error(`Segment tag missing: ${d.field}. Use "Fill missing → HO" to auto-assign.`);
      } else {
        toast.error(typeof d === "string" ? d : "Post failed");
      }
    }
  };

  const reverse = async (id) => {
    try {
      await api.post(`/vouchers/${id}/reverse`, { reason: "Manual reversal" });
      toast.success("Reversed"); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="vouchers-page">
      <DraftsPanel docType="journal" refreshKey={draftsKey} onPosted={() => load()}
        onOpen={(d) => { setForm(d.ui); setDraftId(d.id); setOpenNew(true); }} />
      <PageHeader
        eyebrow="Accounting"
        title="Vouchers & Journals"
        description="Post journal, payment, receipt, contra and adjustment vouchers with full audit trail."
        actions={
          <>
            <Select value={type || "all"} onValueChange={(v) => setType(v === "all" ? "" : v)}>
              <SelectTrigger className="rounded-sm h-9 w-40" data-testid="voucher-type-filter"><SelectValue placeholder="Type" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All types</SelectItem>
                {VOUCHER_TYPES.map((v) => <SelectItem key={v.v} value={v.v}>{v.l}</SelectItem>)}
                <SelectItem value="sales">Sales</SelectItem>
                <SelectItem value="purchase">Purchase</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={async () => {
              const params = { ...(type ? { voucher_type: type } : {}), ...Object.fromEntries(Object.entries(filters).filter(([, v]) => v !== "" && v !== null)) };
              await downloadBlob("/vouchers/export.xlsx", "vouchers.xlsx", params);
            }} className="rounded-sm gap-1.5" data-testid="export-vouchers-xlsx"><span className="text-xs">Excel</span></Button>
            <Button onClick={() => setOpenNew(true)} className="rounded-sm bg-accent hover:bg-accent/90 text-white" data-testid="new-voucher-btn">+ Voucher</Button>
          </>
        }
      />

      {/* Iteration 29: Voucher list filters */}
      <div className="card-sharp p-3 mb-4 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2 items-end" data-testid="voucher-filters">
        <div><Label className="text-[10px] font-mono uppercase">From</Label>
          <Input type="date" value={filters.from_date} onChange={e => setFilters({ ...filters, from_date: e.target.value })} className="h-8" data-testid="vf-from" /></div>
        <div><Label className="text-[10px] font-mono uppercase">To</Label>
          <Input type="date" value={filters.to_date} onChange={e => setFilters({ ...filters, to_date: e.target.value })} className="h-8" data-testid="vf-to" /></div>
        <div><Label className="text-[10px] font-mono uppercase">Party</Label>
          <SearchSelect
            options={[{ value: "__all", label: "Any party" }, ...parties.map((p) => ({ value: p.id, label: p.name, sublabel: p.party_type }))]}
            value={filters.party_id || "__all"}
            onChange={(v) => setFilters({ ...filters, party_id: v === "__all" ? "" : v })}
            testId="vf-party"
            placeholder="Any"
            className="h-8" /></div>
        <div><Label className="text-[10px] font-mono uppercase">Ledger</Label>
          <SearchSelect
            options={[{ value: "__all", label: "Any account" }, ...accounts.map((a) => ({ value: a.id, label: `${a.code} · ${a.name}`, sublabel: a.account_type }))]}
            value={filters.account_id || "__all"}
            onChange={(v) => setFilters({ ...filters, account_id: v === "__all" ? "" : v })}
            testId="vf-ledger"
            placeholder="Any"
            className="h-8" /></div>
        <div><Label className="text-[10px] font-mono uppercase">Narration</Label>
          <Input value={filters.narration} onChange={e => setFilters({ ...filters, narration: e.target.value })} placeholder="contains…" className="h-8" data-testid="vf-narration" /></div>
        <div><Label className="text-[10px] font-mono uppercase">Min ₹</Label>
          <Input type="number" value={filters.min_amount} onChange={e => setFilters({ ...filters, min_amount: e.target.value })} className="h-8" data-testid="vf-min" /></div>
        <div><Label className="text-[10px] font-mono uppercase">Max ₹</Label>
          <Input type="number" value={filters.max_amount} onChange={e => setFilters({ ...filters, max_amount: e.target.value })} className="h-8" data-testid="vf-max" /></div>
        <div className="col-span-2 md:col-span-4 lg:col-span-7 flex gap-2 items-center">
          <label className="flex items-center gap-2 text-xs cursor-pointer border border-border px-2 h-8">
            <input type="checkbox" checked={filters.source === "tally_import"} onChange={e => setFilters({ ...filters, source: e.target.checked ? "tally_import" : "" })} data-testid="vf-tally-only" />
            <span>Tally-imported only</span>
          </label>
          <Button size="sm" onClick={load} data-testid="vf-apply" className="h-8">Apply filters</Button>
          <Button size="sm" variant="outline" onClick={() => { resetFilters(); setTimeout(load, 0); }} data-testid="vf-reset" className="h-8">Reset</Button>
          <span className="ml-auto text-xs text-muted-foreground">{rows.length} vouchers</span>
        </div>
      </div>

      <div className="card-sharp overflow-hidden">
        {selected.size > 0 && (
          <div className="p-3 border-b bg-accent/10 flex items-center justify-between flex-wrap gap-2" data-testid="voucher-bulk-bar">
            <span className="text-sm font-medium">{selected.size} selected</span>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={clearSel} data-testid="voucher-bulk-clear">Clear</Button>
              <Button size="sm" variant="outline" onClick={bulkDelete}
                       className="text-red-600 border-red-500/60 hover:bg-red-50"
                       data-testid="voucher-bulk-delete">
                Erase Selected
              </Button>
            </div>
          </div>
        )}
        <div className="p-3 border-b bg-muted/30 flex items-center justify-end">
          <Button size="sm" variant="outline" onClick={wipeAll}
                    className="text-red-600 border-red-500/60 hover:bg-red-50"
                    data-testid="voucher-wipe-all">
            <Trash2 className="w-3.5 h-3.5 mr-1" /> Erase ALL Vouchers
          </Button>
        </div>
        <table className="w-full dense">
          <thead>
            <tr>
              <th className="w-9"><input type="checkbox"
                                              checked={rows.length > 0 && selected.size === rows.length}
                                              onChange={toggleAll}
                                              data-testid="voucher-select-all" /></th>
              <th>Date</th><th>Voucher</th><th>Type</th><th>Narration</th>
              <th className="text-right">Debit</th><th className="text-right">Credit</th><th className="text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan="8" className="text-center text-muted-foreground py-8">No vouchers yet</td></tr>}
            {rows.map((v) => (
              <tr key={v.id} onClick={() => setOpenDetail(v)} className="cursor-pointer hover:bg-muted/40" data-testid={`voucher-row-${v.voucher_number}`}>
                <td onClick={(e) => e.stopPropagation()} className="w-9">
                  <input type="checkbox"
                         checked={selected.has(v.id)}
                         onChange={() => toggleOne(v.id)}
                         data-testid={`voucher-sel-${v.voucher_number}`} />
                </td>
                <td className="text-xs whitespace-nowrap">{fmtDate(v.date)}</td>
                <td className="font-mono text-xs">{v.voucher_number}</td>
                <td><span className="text-[10px] font-mono uppercase tracking-widest text-accent">{v.voucher_type}</span></td>
                <td className="text-xs text-muted-foreground max-w-xs truncate">{v.narration || "—"}</td>
                <td className="text-right tabular-nums">{fmtINR(v.total_debit)}</td>
                <td className="text-right tabular-nums">{fmtINR(v.total_credit)}</td>
                <td className="text-right space-x-1" onClick={(e) => e.stopPropagation()}>
                  <AttachButton docType="voucher" docId={v.id} docNumber={v.voucher_number} />
                  <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setOpenDetail(v)} data-testid={`view-${v.voucher_number}`}>Open</Button>
                  {v.status === "posted" && !v.locked && (
                    <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => reverse(v.id)} data-testid={`reverse-${v.voucher_number}`}>Reverse</Button>
                  )}
                  {v.status === "reversed" && <span className="text-[10px] font-mono uppercase text-red-500">Reversed</span>}
                  {v.locked && <span className="text-[10px] font-mono uppercase text-muted-foreground">Locked</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Sheet open={openNew} onOpenChange={(o) => { setOpenNew(o); if (!o && draftId) { setDraftId(null); setForm((f) => ({ ...f, narration: "", lines: [{ account_id: "", debit: 0, credit: 0 }, { account_id: "", debit: 0, credit: 0 }] })); } }}>
        <SheetContent side="right" className="w-full sm:max-w-4xl rounded-none overflow-y-auto" data-testid="new-voucher-drawer">
          <SheetHeader className="pb-4 border-b">
            <SheetTitle className="font-heading">Post New Voucher</SheetTitle>
          </SheetHeader>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 my-4">
            <div>
              <Label className="text-[10px] uppercase font-mono tracking-widest">Type</Label>
              <Select value={form.voucher_type} onValueChange={(v) => setForm({ ...form, voucher_type: v })}>
                <SelectTrigger className="rounded-sm mt-1" data-testid="voucher-form-type"><SelectValue /></SelectTrigger>
                <SelectContent>{VOUCHER_TYPES.map((v) => <SelectItem key={v.v} value={v.v}>{v.l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] uppercase font-mono tracking-widest">Branch</Label>
              <Select value={form.branch_id} onValueChange={(v) => setForm({ ...form, branch_id: v })}>
                <SelectTrigger className="rounded-sm mt-1" data-testid="voucher-form-branch"><SelectValue /></SelectTrigger>
                <SelectContent>{branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] uppercase font-mono tracking-widest">Date</Label>
              <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="rounded-sm mt-1" data-testid="voucher-form-date" />
            </div>
            {departments.length > 0 && (
              <div>
                <Label className="text-[10px] uppercase font-mono tracking-widest">Department</Label>
                <Select value={form.department_id || "__none__"} onValueChange={(v) => setForm({ ...form, department_id: v === "__none__" ? "" : v })}>
                  <SelectTrigger className="rounded-sm mt-1" data-testid="voucher-form-department"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">—</SelectItem>
                    {departments.map((d) => <SelectItem key={d.id} value={d.id}>{d.code} · {d.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <div>
            <Label className="text-[10px] uppercase font-mono tracking-widest">Narration</Label>
            <Input value={form.narration} onChange={(e) => setForm({ ...form, narration: e.target.value })} className="rounded-sm mt-1" placeholder="Purpose of this entry" data-testid="voucher-form-narration" />
          </div>

          {/* Iteration 28: entry-level segment tags (required when income/expense lines exist) */}
          <div className={`grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2 p-3 border rounded-sm ${hasIncExpLine() && (!form.business_segment_id || !form.geo_segment_id) ? "border-red-500/40 bg-red-500/5" : "border-border"}`} data-testid="voucher-header-segments">
            <div>
              <Label className="text-[10px] uppercase font-mono tracking-widest">
                Business Segment {hasIncExpLine() && <span className="text-red-500">*</span>}
              </Label>
              <Select value={form.business_segment_id || "__pick"} onValueChange={(v) => setForm({ ...form, business_segment_id: v === "__pick" ? "" : v })}>
                <SelectTrigger className="rounded-sm mt-1" data-testid="voucher-biz-segment"><SelectValue placeholder="Pick a segment…" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__pick">— Pick —</SelectItem>
                  {bizSegments.map(s => <SelectItem key={s.id} value={s.id}>{s.name}{s.is_head_office ? " · HO" : ""}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] uppercase font-mono tracking-widest">
                Geographical / Branch / Region {hasIncExpLine() && <span className="text-red-500">*</span>}
              </Label>
              <Select value={form.geo_segment_id || "__pick"} onValueChange={(v) => setForm({ ...form, geo_segment_id: v === "__pick" ? "" : v })}>
                <SelectTrigger className="rounded-sm mt-1" data-testid="voucher-geo-segment"><SelectValue placeholder="Pick a location…" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__pick">— Pick —</SelectItem>
                  {geoSegments.map(s => <SelectItem key={s.id} value={s.id}>{s.name}{s.is_head_office ? " · HO" : ""} <span className="text-[10px] font-mono uppercase text-muted-foreground ml-1">{s.kind}</span></SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-2 flex items-center gap-2 text-[11px] text-muted-foreground">
              <span>Segment tags are required at voucher level whenever any income/expense line is present.</span>
              <Button size="sm" variant="ghost" onClick={autoFillHO} className="text-xs h-6 ml-auto" data-testid="voucher-fill-ho">Fill missing → HO / Corporate</Button>
            </div>
          </div>

          <div className="mt-6 border rounded-sm overflow-hidden">
            <table className="w-full dense">
              <thead>
                <tr><th style={{ width: 240 }}>Account</th><th>Narration</th><th style={{ width: 140 }}>Party</th><th className="text-right" style={{ width: 130 }}>Debit</th><th className="text-right" style={{ width: 130 }}>Credit</th><th style={{ width: 40 }}></th></tr>
              </thead>
              <tbody>
                {form.lines.map((ln, i) => (
                  <tr key={i}>
                    <td className="p-1">
                      <SearchSelect
                        options={accounts.map((a) => ({ value: a.id, label: `${a.code} · ${a.name}`, sublabel: a.account_type }))}
                        value={ln.account_id}
                        onChange={(v) => updateLine(i, { account_id: v })}
                        testId={`vline-account-${i}`}
                        placeholder="Select"
                        className="h-8" />
                    </td>
                    <td className="p-1"><Input value={ln.narration || ""} onChange={(e) => updateLine(i, { narration: e.target.value })} className="rounded-sm h-8 text-xs" placeholder="line narration" /></td>
                    <td className="p-1">
                      <SearchSelect
                        options={[{ value: "none", label: "—" }, ...parties.map((p) => ({ value: p.id, label: p.name, sublabel: p.party_type }))]}
                        value={ln.party_id || "none"}
                        onChange={(v) => updateLine(i, { party_id: v === "none" ? "" : v, party_type: parties.find((p) => p.id === v)?.party_type })}
                        placeholder="—"
                        className="h-8 text-xs" />
                    </td>
                    <td className="p-1"><Input type="number" step="0.01" value={ln.debit || ""} onChange={(e) => updateLine(i, { debit: Number(e.target.value), credit: 0 })} className="rounded-sm h-8 text-right tabular-nums" data-testid={`vline-debit-${i}`} /></td>
                    <td className="p-1"><Input type="number" step="0.01" value={ln.credit || ""} onChange={(e) => updateLine(i, { credit: Number(e.target.value), debit: 0 })} className="rounded-sm h-8 text-right tabular-nums" data-testid={`vline-credit-${i}`} /></td>
                    <td className="text-center">{form.lines.length > 2 && <button onClick={() => removeLine(i)} className="text-muted-foreground hover:text-destructive"><Trash2 className="w-3.5 h-3.5" /></button>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="border-t flex items-center justify-between p-3 bg-muted/40 text-sm">
              <Button variant="ghost" size="sm" onClick={addLine} className="rounded-sm gap-1"><Plus className="w-3.5 h-3.5" /> Add line</Button>
              <div className="flex gap-6 tabular-nums">
                <div>DR: <span className="font-mono">{fmtINR(totalDr)}</span></div>
                <div>CR: <span className="font-mono">{fmtINR(totalCr)}</span></div>
                <div className={balanced ? "text-emerald-600" : "text-destructive"}>Diff: <span className="font-mono">{fmtINR(totalDr - totalCr)}</span></div>
              </div>
            </div>
          </div>

          <div className="mt-6 flex gap-2 justify-end">
            <Button variant="outline" onClick={() => setOpenNew(false)} className="rounded-sm">Cancel</Button>
            <Button variant="outline" onClick={saveAsDraft} className="rounded-sm" data-testid="voucher-save-draft">{draftId ? "Update Draft" : "Save as Draft"}</Button>
            <Button onClick={submit} disabled={!balanced} className="rounded-sm bg-accent hover:bg-accent/90 text-white" data-testid="voucher-submit">Post Voucher</Button>
          </div>
          <div className="mt-4"><StagedAttachments ref={stagedRef} label="Attach voucher supporting documents" /></div>
        </SheetContent>
      </Sheet>

      <Sheet open={!!openDetail} onOpenChange={(o) => !o && setOpenDetail(null)}>
        <SheetContent side="right" className="w-full sm:max-w-xl rounded-none overflow-y-auto space-y-4">
          <SheetHeader><SheetTitle className="font-heading">Voucher {openDetail?.voucher_number}</SheetTitle></SheetHeader>
          {openDetail && (
            <>
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div><span className="text-muted-foreground">Date</span><div className="font-medium">{fmtDate(openDetail.date)}</div></div>
                <div><span className="text-muted-foreground">Type</span><div className="font-mono uppercase text-accent text-[10px]">{openDetail.voucher_type}</div></div>
                <div className="col-span-2"><span className="text-muted-foreground">Narration</span><div>{openDetail.narration || "—"}</div></div>
              </div>
              <table className="w-full dense text-xs border rounded-sm">
                <thead><tr><th>Account</th><th className="text-right">Debit</th><th className="text-right">Credit</th></tr></thead>
                <tbody>{openDetail.lines.map((l, i) => (
                  <tr key={i}><td><span className="font-mono text-[10px] text-muted-foreground mr-1">{l.account_code}</span>{l.account_name}</td><td className="text-right tabular-nums">{fmtINR(l.debit)}</td><td className="text-right tabular-nums">{fmtINR(l.credit)}</td></tr>
                ))}</tbody>
              </table>
              <Attachments docType="voucher" docId={openDetail.id} />
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
